import { ScheduleServiceDialog } from "@/components/site/ScheduleServiceDialog";
import { useParams } from "react-router-dom";
import { Link, Navigate } from "@/i18n/Link";
import { ArrowRight, Phone, MapPin, Wrench, ShieldCheck, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/site/PageLayout";
import { Seo, breadcrumbJsonLd, organizationJsonLd, serviceJsonLd } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { SITE_PHONE, SITE_PHONE_DISPLAY, SITE_URL } from "@/routes-data";
import { getCityL } from "@/locations/cities";
import { localizedAssetUrl } from "@/i18n/assets";
import { getProductL, getProducts, interpolate } from "@/locations/products";
import { getCityProductContent } from "@/locations/city-product-content";
import { useCopy } from "@/i18n/LocaleContext";
import { useLocale } from "@/i18n/LocaleContext";

const COPY = {
  en: {
    home: "Home",
    locations: "Locations",
    scheduleService: "Schedule service",
    quickFacts: (city: string) => `${city} quick facts`,
    serviceArea: "Service area",
    distFromShop: "Distance from our shop",
    routesToUs: "Routes to us",
    zipsCovered: "ZIPs covered",
    serviceOffered: "Service offered",
    scheduleOnline: "Schedule online",
    whatIncluded: (label: string, city: string) => `What's included in ${label.toLowerCase()} for ${city}`,
    expDisclaimer: (city: string) => `Sixty-plus years of combined trailer experience, OEM Dexter inventory, and a service approach that treats ${city} customers the way we'd want to be treated.`,
    whyChoose: (city: string, label: string) => `Why ${city} customers choose Axle Inc. for ${label.toLowerCase()}`,
    whyChooseSub: (city: string) => `The same reasons ${city} trailer owners and fleets have trusted us for decades.`,
    faq: (label: string, city: string) => `${label} in ${city} - frequently asked questions`,
    otherServices: (city: string) => `Other services in ${city}`,
    otherServicesSub: (city: string) => `Cross-link to the rest of what we offer ${city} trailer owners.`,
    otherServiceLabel: (label: string, city: string) => `${label} in ${city}`,
    backToOverview: (city: string) => `Back to ${city} overview`,
    costLink: "See what axle replacement costs",
    emergencyLink: (city: string) => `Emergency & same-day repair for ${city}`,
    readyHeading: (city: string) => `Ready to get started, ${city}?`,
    readySub: "Schedule online, call the shop, or drop in. We'll be straight with you about what's needed and what isn't.",
    marketHeading: (city: string) => `What we see from the ${city} market`,
    customersHeading: (label: string, city: string) => `${label} for ${city} customers`
  },
  es: {
    home: "Inicio",
    locations: "Ubicaciones",
    scheduleService: "Programar servicio",
    quickFacts: (city: string) => `Datos rápidos de ${city}`,
    serviceArea: "Área de servicio",
    distFromShop: "Distancia desde nuestro taller",
    routesToUs: "Rutas para llegar",
    zipsCovered: "Códigos postales cubiertos",
    serviceOffered: "Servicio ofrecido",
    scheduleOnline: "Programar en línea",
    whatIncluded: (label: string, city: string) => `Qué incluye ${label.toLowerCase()} en ${city}`,
    expDisclaimer: (city: string) => `Más de sesenta años de experiencia combinada en remolques, inventario original Dexter y un enfoque de servicio que trata a los clientes de ${city} como nos gustaría ser tratados.`,
    whyChoose: (city: string, label: string) => `Por qué los clientes de ${city} eligen Axle Inc. para ${label.toLowerCase()}`,
    whyChooseSub: (city: string) => `Las mismas razones por las que los propietarios de remolques y flotas de ${city} han confiado en nosotros durante décadas.`,
    faq: (label: string, city: string) => `${label} en ${city} - preguntas frecuentes`,
    otherServices: (city: string) => `Otros servicios en ${city}`,
    otherServicesSub: (city: string) => `Vea el resto de lo que ofrecemos a los propietarios de remolques en ${city}.`,
    otherServiceLabel: (label: string, city: string) => `${label} en ${city}`,
    backToOverview: (city: string) => `Volver a la vista general de ${city}`,
    costLink: "Vea cuánto cuesta el reemplazo de ejes",
    emergencyLink: (city: string) => `Reparación de emergencia y el mismo día para ${city}`,
    readyHeading: (city: string) => `¿Listo para comenzar, ${city}?`,
    readySub: "Programe en línea, llame al taller o visítenos. Seremos honestos con usted sobre lo que es necesario y lo que no.",
    marketHeading: (city: string) => `Lo que vemos del mercado de ${city}`,
    customersHeading: (label: string, city: string) => `${label} para clientes de ${city}`
  }
};

export default function CityProductPage() {
  const { slug, product } = useParams<{ slug: string; product: string }>();
  const { locale } = useLocale();
  const t = useCopy(COPY);
  const city = slug ? getCityL(slug, locale) : undefined;
  const productDef = product ? getProductL(product, locale) : undefined;
  if (!city) return <Navigate to="/locations/" replace />;
  if (!productDef) return <Navigate to={`/locations/${slug}/`} replace />;

  const path = `/locations/${city.slug}/${productDef.slug}/`;
  const image = `${locale === "es" ? "/es" : ""}/locations/${city.slug}-${productDef.slug}.svg`;
  const cityProductPara = getCityProductContent(locale)[city.slug]?.[productDef.key] ?? "";
  const cityLabel = `${city.name}, ${city.state}`;

  const trail = [
    { name: t.home, path: "/" },
    { name: t.locations, path: "/locations/" },
    { name: cityLabel, path: `/locations/${city.slug}/` },
    { name: productDef.shortLabel, path },
  ];

  const title = productDef.metaTitle(city.name, city.state);
  const description = productDef.metaDesc(city.name, city.state);
  const otherProducts = getProducts(locale).filter((p) => p.key !== productDef.key);

  return (
    <PageLayout>
      <Seo
        title={title}
        description={description}
        path={path}
        image={image}
        jsonLd={[
          organizationJsonLd(),
          breadcrumbJsonLd(trail),
          serviceJsonLd({
            name: `${productDef.label} - ${cityLabel}`,
            description,
            path,
            serviceType: productDef.serviceType,
          }),
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": `${SITE_URL}${path}#localbusiness`,
            name: `Axle Inc. - ${productDef.label} for ${cityLabel}`,
            description,
            url: `${SITE_URL}${path}`,
            telephone: SITE_PHONE,
            image: `${SITE_URL}${image}`,
            priceRange: "$$",
            address: {
              "@type": "PostalAddress",
              streetAddress: "29473 US-33",
              addressLocality: "Elkhart",
              addressRegion: "IN",
              postalCode: "46517",
              addressCountry: "US",
            },
            areaServed: {
              "@type": "City",
              name: city.name,
              address: {
                "@type": "PostalAddress",
                addressLocality: city.name,
                addressRegion: city.state,
                postalCode: city.zips.split(",")[0].trim(),
                addressCountry: "US",
              },
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: productDef.faqs.map((f) => ({
              "@type": "Question",
              name: interpolate(f.q, cityLabel),
              acceptedAnswer: { "@type": "Answer", text: interpolate(f.a, cityLabel) },
            })),
          },
        ]}
      />

      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="mb-6 [&_a]:text-secondary-foreground/70 [&_a:hover]:text-white [&_span]:text-white">
            <Breadcrumbs trail={trail} />
          </div>
          <p className="text-xs md:text-sm uppercase tracking-[0.22em] text-white font-semibold mb-4">
            <MapPin className="inline-block h-3.5 w-3.5 mr-1.5 -mt-0.5" />
            {productDef.label} &middot; {cityLabel} &middot; {city.county}
          </p>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight max-w-4xl">
            {productDef.h1(city.name, city.state)}
          </h1>
          <p className="mt-5 text-lg text-secondary-foreground/85 max-w-3xl leading-relaxed">
            {cityProductPara}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <ScheduleServiceDialog>
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                {t.scheduleService} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </ScheduleServiceDialog>
            <Button asChild size="lg" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10">
              <a href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}>
                <Phone className="mr-1.5 h-4 w-4" /> {SITE_PHONE_DISPLAY}
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-12 md:py-16">
        <img
          src={localizedAssetUrl(`/locations/${city.slug}-${productDef.slug}.svg`, locale)}
          alt={`Axle Inc. ${productDef.label.toLowerCase()} serving ${cityLabel}`}
          width={1200}
          height={630}
          className="w-full rounded-md border border-border bg-muted"
          loading="eager"
        />
      </section>

      <section className="container mx-auto px-4 pb-12 md:pb-16">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-6 leading-relaxed">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
              {t.customersHeading(productDef.label, city.name)}
            </h2>
            <p className="text-muted-foreground">{productDef.pitch.replace("${c}", city.name)}</p>
            <h3 className="text-xl font-semibold tracking-tight pt-4">{t.marketHeading(city.name)}</h3>
            <p className="text-muted-foreground">{city.localContext}</p>
            <p className="text-muted-foreground">{city.midContent}</p>
          </div>
          <aside className="bg-card border border-border rounded-md p-6 space-y-4 self-start">
            <h3 className="font-semibold text-lg">{t.quickFacts(city.name)}</h3>
            <dl className="text-sm space-y-3">
              <div>
                <dt className="text-muted-foreground">{t.serviceArea}</dt>
                <dd className="font-semibold">{cityLabel}, {city.county}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t.distFromShop}</dt>
                <dd className="font-semibold">{city.distance} miles {city.direction}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t.routesToUs}</dt>
                <dd className="font-semibold">{city.highways}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t.zipsCovered}</dt>
                <dd className="font-semibold">{city.zips}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t.serviceOffered}</dt>
                <dd className="font-semibold">{productDef.label}</dd>
              </div>
            </dl>
            <ScheduleServiceDialog>
              <Button size="sm" className="w-full bg-primary text-primary-foreground hover:bg-primary/90">{t.scheduleOnline}</Button>
            </ScheduleServiceDialog>
          </aside>
        </div>
      </section>

      <section className="bg-muted/40 border-y border-border">
        <div className="container mx-auto px-4 py-16">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3 max-w-3xl">
            {t.whatIncluded(productDef.label, city.name)}
          </h2>
          <p className="text-muted-foreground max-w-3xl mb-10">
            {t.expDisclaimer(city.name)}
          </p>
          <div className="grid gap-6 md:grid-cols-2">
            {productDef.features.map((f, i) => (
              <article key={f.title} className="border border-border rounded-md p-6 bg-card">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center font-bold">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1.5">{f.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-20">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3 max-w-3xl">
          {t.whyChoose(city.name, productDef.label)}
        </h2>
        <p className="text-muted-foreground max-w-3xl mb-10">
          {t.whyChooseSub(city.name)}
        </p>
        <div className="grid gap-6 md:grid-cols-2">
          {city.whyChoose.map((reason, i) => (
            <div key={i} className="flex gap-4 bg-card border border-border rounded-md p-5">
              <ShieldCheck className="shrink-0 h-6 w-6 text-primary mt-0.5" />
              <p className="text-sm leading-relaxed">{reason}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container mx-auto px-4 pb-16 md:pb-20 max-w-3xl">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">
          {t.faq(productDef.label, city.name)}
        </h2>
        <div className="divide-y divide-border border-y border-border">
          {productDef.faqs.map((f) => (
            <details key={f.q} className="group py-5">
              <summary className="cursor-pointer list-none flex justify-between items-start gap-4 font-medium">
                {interpolate(f.q, cityLabel)}
                <span className="text-primary text-xl leading-none mt-0.5 group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-muted-foreground leading-relaxed">{interpolate(f.a, cityLabel)}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="bg-muted/40 border-y border-border">
        <div className="container mx-auto px-4 py-12 md:py-16">
          <h2 className="text-xl md:text-2xl font-bold tracking-tight mb-2">
            {t.otherServices(city.name)}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            {t.otherServicesSub(city.name)}
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            {otherProducts.map((p) => (
              <Link
                key={p.key}
                to={`/locations/${city.slug}/${p.slug}/`}
                className="flex items-center justify-between gap-3 bg-card border border-border hover:border-primary hover:shadow-sm rounded-md px-5 py-4 transition-all group"
              >
                <span className="font-medium text-sm">
                  {t.otherServiceLabel(p.label, city.name)}
                </span>
                <ChevronRight className="h-4 w-4 text-primary group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
            <Link to={`/locations/${city.slug}/`} className="text-sm text-primary-strong hover:underline underline-offset-4 inline-flex items-center gap-1">
              <ArrowRight className="h-3.5 w-3.5" /> {t.backToOverview(city.name)}
            </Link>
            {productDef.slug === "trailer-repair" && (
              <>
                <Link to="/trailer-axle-replacement-cost/" className="text-sm text-primary-strong hover:underline underline-offset-4 inline-flex items-center gap-1">
                  <ArrowRight className="h-3.5 w-3.5" /> {t.costLink}
                </Link>
                <Link to="/services/emergency-same-day-trailer-repair/" className="text-sm text-primary-strong hover:underline underline-offset-4 inline-flex items-center gap-1">
                  <ArrowRight className="h-3.5 w-3.5" /> {t.emergencyLink(city.name)}
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="bg-primary-deep text-primary-foreground">
        <div className="container mx-auto px-4 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <Wrench className="h-8 w-8 mt-1 shrink-0" />
            <div>
              <h2 className="text-2xl md:text-3xl font-bold">{t.readyHeading(city.name)}</h2>
              <p className="mt-1 text-primary-foreground">{t.readySub}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <ScheduleServiceDialog>
              <Button size="lg" className="bg-white text-primary-strong hover:bg-white/90">{t.scheduleOnline}</Button>
            </ScheduleServiceDialog>
            <Button asChild size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white/10">
              <a href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}>
                <Phone className="mr-1.5 h-4 w-4" /> {SITE_PHONE_DISPLAY}
              </a>
            </Button>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
